/**
 * usePatrolDetail Hook
 *
 * React Query hook for fetching detailed patrol information including
 * checklists and checkpoints. Used by PatrolDetailModal component.
 *
 * Features:
 * - On-demand fetch (no polling - modal is temporary)
 * - Conditional fetching based on scheduleAssignmentId
 * - Returns checklist/checkpoint data from C# backend
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getPatrolDetails, PatrolDetailsResponse } from '../../../../../api/activityReport/getPatrolDetails';
import { Checklist, Checkpoint, CheckpointStatus, CheckpointDropdown, CheckpointAttachment } from '../../../../../api/activityReport/getActivityReport';

/** Query key for patrol details */
export const PATROL_DETAIL_QUERY_KEY = ['patrol', 'detail'];

/** Stale time - 30 seconds (patrol data can change while viewing) */
const STALE_TIME = 30 * 1000;

/**
 * Checkpoint completion status for display
 */
export type CheckpointDisplayStatus = 'clear' | 'warning' | 'emergency' | 'info' | 'incomplete';

/**
 * Transformed checkpoint with display-friendly properties
 */
export interface TransformedCheckpoint {
  checkpointId: number;
  checklistId: number;
  name: string;
  description: string;
  responseTypeName: string;
  statuses: CheckpointStatus[];
  selectedStatusId: number | null;
  selectedStatus: CheckpointStatus | null;
  displayStatus: CheckpointDisplayStatus;
  comment: string;
  time: string;
  dateTime: string | null;
  isCompleted: boolean;
  /** Dropdown options for Dropdown/DropdownMultiSelect response types */
  dropdowns: CheckpointDropdown[];
  /** Selected dropdown IDs */
  selectedDropdownIds: number[];
  /** Attachments for Attachment/Signature response types */
  attachments: CheckpointAttachment[];
}

/**
 * Transformed checklist with computed properties
 */
export interface TransformedChecklist {
  checklistId: number;
  name: string;
  description: string;
  type: string;
  checkpoints: TransformedCheckpoint[];
  completedCount: number;
  totalCount: number;
  isComplete: boolean;
}

/**
 * Overall patrol progress summary
 */
export interface PatrolProgress {
  totalCheckpoints: number;
  completedCheckpoints: number;
  percentComplete: number;
  hasWarnings: boolean;
  hasEmergencies: boolean;
}

export interface UsePatrolDetailOptions {
  /** Schedule assignment ID to fetch details for */
  scheduleAssignmentId: number | null;
  /** Whether to enable the query (default: true when ID provided) */
  enabled?: boolean;
}

export interface UsePatrolDetailResult {
  /** Transformed checklists with checkpoints */
  checklists: TransformedChecklist[];
  /** Overall progress summary */
  progress: PatrolProgress;
  /** General comment for the patrol */
  generalComment: string | null;
  /** Raw API response */
  rawData: PatrolDetailsResponse | null;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Manually trigger a refresh */
  refetch: () => void;
}

/**
 * Map status number to display status category
 * Based on Checklist.CheckpointStatus table:
 * - 1: Clear/OK (green)
 * - 2: Warning (orange)
 * - 3: Emergency/Alert (red)
 */
function mapStatusNumberToDisplayStatus(statusNum: number): CheckpointDisplayStatus {
  switch (statusNum) {
    case 1:
      return 'clear';
    case 2:
      return 'warning';
    case 3:
      return 'emergency';
    default:
      return 'clear';
  }
}

/**
 * Determine the display status for a checkpoint based on its type and completion state
 */
function getCheckpointDisplayStatus(
  checkpoint: Checkpoint,
  selectedStatus: CheckpointStatus | null,
  isCompleted: boolean
): CheckpointDisplayStatus {
  if (!isCompleted) {
    return 'incomplete';
  }

  const responseType = checkpoint.ResponseTypeName?.toLowerCase() || '';

  // For Status type, use the selected status's StatusNumber
  if (responseType === 'status' && selectedStatus) {
    return mapStatusNumberToDisplayStatus(selectedStatus.StatusNumber);
  }

  // For all other types (Comment, Time, DateTime, Dropdown, Attachment, Signature),
  // when completed, show as 'clear' (green checkmark) since they don't have severity levels
  return 'clear';
}

/**
 * Determine if a checkpoint is completed based on its response type
 * Different checkpoint types store their completion data differently
 */
function isCheckpointCompleted(checkpoint: Checkpoint): boolean {
  const responseType = checkpoint.ResponseTypeName?.toLowerCase() || '';

  switch (responseType) {
    case 'status':
      // Status type: completed when a status is selected
      return checkpoint.SelectedStatusID !== null && checkpoint.SelectedStatusID !== 0;

    case 'comment':
      // Comment type: completed when comment has content
      return checkpoint.Comment !== null && checkpoint.Comment.trim() !== '';

    case 'time':
      // Time type: completed when time is set
      return checkpoint.Time !== null && checkpoint.Time.trim() !== '';

    case 'datetime':
      // DateTime type: completed when datetime is set
      return checkpoint.DateTime !== null;

    case 'dropdown':
    case 'dropdownmultiselect':
      // Dropdown types: completed when at least one dropdown is selected
      return Array.isArray(checkpoint.SelectedDropdownIDs) && checkpoint.SelectedDropdownIDs.length > 0;

    case 'attachment':
    case 'signature':
      // Attachment/Signature types: completed when at least one attachment exists
      return Array.isArray(checkpoint.Attachments) && checkpoint.Attachments.length > 0;

    default:
      // Fallback: check if any response data exists
      return (
        (checkpoint.SelectedStatusID !== null && checkpoint.SelectedStatusID !== 0) ||
        (checkpoint.Comment !== null && checkpoint.Comment.trim() !== '') ||
        (checkpoint.Time !== null && checkpoint.Time.trim() !== '') ||
        checkpoint.DateTime !== null
      );
  }
}

/**
 * Transform API checkpoint to frontend format
 */
function transformCheckpoint(checkpoint: Checkpoint): TransformedCheckpoint {
  const selectedStatus = checkpoint.Status?.find(
    (s) => s.StatusID === checkpoint.SelectedStatusID
  ) || null;

  const isCompleted = isCheckpointCompleted(checkpoint);

  return {
    checkpointId: checkpoint.CheckpointID,
    checklistId: checkpoint.ChecklistID,
    name: checkpoint.Name,
    description: checkpoint.Description,
    responseTypeName: checkpoint.ResponseTypeName,
    statuses: checkpoint.Status || [],
    selectedStatusId: checkpoint.SelectedStatusID,
    selectedStatus,
    displayStatus: getCheckpointDisplayStatus(checkpoint, selectedStatus, isCompleted),
    comment: checkpoint.Comment || '',
    time: checkpoint.Time || '',
    dateTime: checkpoint.DateTime,
    isCompleted,
    dropdowns: checkpoint.Dropdowns || [],
    selectedDropdownIds: checkpoint.SelectedDropdownIDs || [],
    attachments: checkpoint.Attachments || [],
  };
}

/**
 * Transform API checklist to frontend format
 */
function transformChecklist(checklist: Checklist): TransformedChecklist {
  const checkpoints = checklist.Checkpoints?.map(transformCheckpoint) || [];
  const completedCount = checkpoints.filter((cp) => cp.isCompleted).length;
  const totalCount = checkpoints.length;

  return {
    checklistId: checklist.ChecklistID,
    name: checklist.Name,
    description: checklist.Description,
    type: checklist.Type,
    checkpoints,
    completedCount,
    totalCount,
    isComplete: totalCount > 0 && completedCount === totalCount,
  };
}

/**
 * Calculate overall patrol progress
 */
function calculateProgress(checklists: TransformedChecklist[]): PatrolProgress {
  let totalCheckpoints = 0;
  let completedCheckpoints = 0;
  let hasWarnings = false;
  let hasEmergencies = false;

  for (const checklist of checklists) {
    totalCheckpoints += checklist.totalCount;
    completedCheckpoints += checklist.completedCount;

    for (const checkpoint of checklist.checkpoints) {
      if (checkpoint.displayStatus === 'warning') hasWarnings = true;
      if (checkpoint.displayStatus === 'emergency') hasEmergencies = true;
    }
  }

  const percentComplete = totalCheckpoints > 0
    ? Math.round((completedCheckpoints / totalCheckpoints) * 100)
    : 0;

  return {
    totalCheckpoints,
    completedCheckpoints,
    percentComplete,
    hasWarnings,
    hasEmergencies,
  };
}

/**
 * Hook to fetch detailed patrol information
 *
 * @param options - Configuration options including scheduleAssignmentId
 * @returns Patrol details with loading/error states
 */
export function usePatrolDetail(
  options: UsePatrolDetailOptions
): UsePatrolDetailResult {
  const { scheduleAssignmentId, enabled = true } = options;

  // Only fetch when we have a valid ID
  const shouldFetch = enabled && scheduleAssignmentId !== null && scheduleAssignmentId > 0;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [...PATROL_DETAIL_QUERY_KEY, scheduleAssignmentId],
    queryFn: () => getPatrolDetails(scheduleAssignmentId!),
    enabled: shouldFetch,
    staleTime: STALE_TIME,
    // Don't refetch on window focus for modal - user can manually refresh
    refetchOnWindowFocus: false,
  });

  // Transform checklists
  const checklists = useMemo(() => {
    if (!data?.Checklists) return [];
    return data.Checklists.map(transformChecklist);
  }, [data?.Checklists]);

  // Calculate progress
  const progress = useMemo(() => {
    return calculateProgress(checklists);
  }, [checklists]);

  return {
    checklists,
    progress,
    generalComment: data?.GeneralComment ?? null,
    rawData: data ?? null,
    isLoading: shouldFetch && isLoading,
    isFetching,
    isError,
    error: error instanceof Error ? error : null,
    refetch,
  };
}

export default usePatrolDetail;
